import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import FilterPane from '../components/FilterPane';
import SearchBar from '../components/SearchBar';
import CarCard from '../components/CarCard';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';

export default function BrowseCars() {
  const [cars, setCars] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const carsPerPage = 6;
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;

    // Whenever the search or filters change, reset back to page 1
    setCurrentPage(1);

    const fetchCars = async () => {
      if (!isMounted) return;
      setLoading(true);
      try {
        let endpoint = `${process.env.REACT_APP_API_URL}/api/cars`;
        const params = new URLSearchParams(location.search);
        if (params.toString()) endpoint += `?${params.toString()}`;

        console.log('API Endpoint:', endpoint);
        const res = await axios.get(endpoint, { timeout: 5000 });
        console.log('API Response:', res.data);
        
        if (isMounted) {
          if (res.data.length === 0) {
            setError('No cars found matching your criteria.');
            setCars([]);
            setTotalPages(1);
          } else {
            setCars(res.data);
            setTotalPages(Math.ceil(res.data.length / carsPerPage));
            setError('');
          }
        }
      } catch (err) {
        console.error('Fetch Error:', err.message, err.response?.status, err.response?.data);
        if (isMounted) {
          setError('Failed to load cars. Check console for details.');
          setCars([]);
          setTotalPages(1);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchCars();

    return () => {
      isMounted = false;
    };
  }, [location.search]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const paginatedCars = cars.slice((currentPage - 1) * carsPerPage, currentPage * carsPerPage);

  return (
    <div className="bg-gray-50 text-gray-900 min-h-screen selection:bg-red-600 selection:text-white antialiased overflow-x-hidden">
      <Navbar />
      <div className="max-w-7xl mx-auto py-12 px-6 sm:px-8">
        
        {/* Descriptive Top Panel */}
        <div className="bg-white p-6 sm:p-10 rounded-2xl border border-gray-100 shadow-sm text-justify mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-8 w-1.5 bg-red-600 rounded"></div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-blue-900 tracking-tight font-sans">
              BROWSE ALL CARS
            </h1>
          </div>
          <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-light">
            Welcome to Sajies Global Synergy Limited – Your Trusted Place for Quality Foreign Used Cars and Brand New Cars in Nigeria. Find great deals on well-maintained and affordable cars from top brands like Toyota, Lexus, Mercedes-Benz, Ford, Honda, and many more. You can <a href="/book-inspection" className="text-red-600 font-semibold hover:underline">book an inspection</a> and enjoy a smooth and stress-free car-buying process.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-6 border-t border-gray-100 pt-6 text-xs sm:text-sm font-semibold text-blue-950">
            <li className="flex items-center">✨ Vehicle Diagnosis</li>
            <li className="flex items-center">🛡 Vehicle Insurance</li>
            <li className="flex items-center">🎨 Vehicle Customization</li>
            <li className="flex items-center">🔧 Servicing</li>
            <li className="flex items-center">📝 Vehicle Registration</li>
          </ul>
        </div>

        {/* Global SearchBar */}
        <SearchBar />

        <div className="flex flex-col lg:flex-row gap-8 mt-10">
          {/* Responsive Left Filters */}
          <div className="w-full lg:w-1/4">
            <FilterPane />
          </div>
          
          {/* Right Product Grid */}
          <div className="w-full lg:w-3/4">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="h-8 w-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-500 font-light text-sm">Searching inventory...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <span className="text-4xl mb-3">🚗🔍</span>
                <p className="text-red-600 font-bold text-center mb-1">{error}</p>
                <p className="text-xs text-gray-400">Modify your filters or change keywords and try again.</p>
              </div>
            ) : (
              <div className="space-y-12">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {paginatedCars.length > 0 ? (
                    paginatedCars.map(car => (
                      <div 
                        key={car.id} 
                        className="bg-white rounded-xl shadow-sm border border-gray-100 p-2 hover:scale-[1.01] transition-transform duration-300"
                      >
                        <CarCard car={car} />
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center col-span-full py-16">No cars found matching requirements.</p>
                  )}
                </div>
                
                {/* Mobile-Wrap safe pagination panel */}
                {totalPages > 1 && (
                  <div className="flex flex-wrap gap-2 justify-center max-w-full px-4 pt-4">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                          currentPage === page
                            ? 'bg-blue-900 text-white shadow-md'
                            : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
